import { v4 as uuidv4 } from 'uuid';
import type {
  ChecklistIdMapping,
  RepeatableItemIdTree,
} from '../../../services/checklist-http.service';
import { ChecklistFieldType } from '../types/ChecklistFieldType.enum';
import { ChecklistItemStatus } from '../types/ChecklistItemStatus.enum';
import { ChecklistStatus } from '../types/ChecklistStatus.enum';
import { EnrichedPublishedTemplate } from '../types/ChecklistTemplate';
import { Checklist, ChecklistItem } from '../types/ChecklistTypes';
import { CanonicalTemplate } from '../types/TemplateTypes';
import { withDerivedProgress } from './checklist-progress';
import {
  repeatableInputValues,
  type RepeatableInputPayload,
} from './repeatable-input-values';

export interface InitialRepeatableOccurrence {
  sectionId: string;
  prototypeId: string;
  idTree: RepeatableItemIdTree;
}

export function startChecklistFromTemplate(
  template: EnrichedPublishedTemplate,
  entryTime: string,
  isDrill = false
): [Checklist, ChecklistIdMapping, InitialRepeatableOccurrence[]] {
  const canonical = JSON.parse(template.content) as CanonicalTemplate;

  const checklistIdMapping: ChecklistIdMapping = {
    checklistId: uuidv4(),
    sectionIds: {},
    itemIds: {},
    fieldIds: {},
    customInputIds: {},
  };
  const initialRepeatableOccurrences: InitialRepeatableOccurrence[] = [];

  const sections = (canonical.sections ?? []).map(section => {
    const newSectionId = uuidv4();
    checklistIdMapping.sectionIds[section.id] = newSectionId;

    const items = (section.items ?? []).flatMap(item => {
      const newItemId = uuidv4();
      checklistIdMapping.itemIds[item.id] = newItemId;

      const fields = (item.fields ?? []).map(field => {
        const newFieldId = uuidv4();
        checklistIdMapping.fieldIds[field.id] = newFieldId;

        return {
          id: newFieldId,
          itemId: newItemId,
          type: field.type ?? ChecklistFieldType.Text,
          position: field.position,
          data: field.data,
        };
      });

      const customInputs = (item.customInputs ?? []).map(customInput => {
        const newCustomInputId = uuidv4();
        if (customInput.id) {
          checklistIdMapping.customInputIds[customInput.id] = newCustomInputId;
        }

        return {
          id: newCustomInputId,
          itemId: newItemId,
          customInputTypeId: customInput.customInputTypeId,
          inputContent: customInput.inputContent,
          position: customInput.position,
          payload: 'null',
          sourceCustomInputId: customInput.sourceId ?? customInput.id,
          requiredInput: customInput.requiredInput ?? false,
          slotId: customInput.slotId,
        };
      });

      const slotSelections = (item.selectedSlotIds ?? []).map(
        (slotId, index) => ({
          itemId: newItemId,
          slotId,
          position: index + 1,
          metadata: 'null',
          payload: 'null',
        })
      );

      const isRepeatablePrototype = item.isRepeatable === true;
      const repeatableGroupId =
        item.repeatableGroupId ?? (isRepeatablePrototype ? item.id : undefined);

      const prototype: ChecklistItem = {
        id: newItemId,
        sectionId: newSectionId,
        title: item.title,
        position: item.position,
        status: ChecklistItemStatus.Unchecked,
        logbookTemplateId: item.logbookTemplateId ?? undefined,
        logbookTemplateName: item.logbookTemplateName ?? undefined,
        remarks: undefined,
        fields,
        customInputs,
        slotSelections,
        isRepeatablePrototype,
        editableHeader: item.editableHeader === true,
        header: undefined,
        repeatableTitle: item.repeatableTitle ?? undefined,
        repeatableGroupId,
        repeatableOccurrencePosition: undefined,
        statusChanged: undefined,
        entryTime: undefined,
        undoTime: undefined,
        completionTime: undefined,
      };

      if (!isRepeatablePrototype) {
        return [prototype];
      }

      const { occurrence, idTree } = buildOccurrence(
        prototype,
        newSectionId,
        1,
        {}
      );
      initialRepeatableOccurrences.push({
        sectionId: newSectionId,
        prototypeId: newItemId,
        idTree,
      });

      return [prototype, occurrence];
    });

    return {
      id: newSectionId,
      checklistId: checklistIdMapping.checklistId,
      title: section.title,
      position: section.position,
      logbookTemplateId: section.logbookTemplateId ?? undefined,
      items,
      completed: false,
      lastModified: undefined,
    };
  });

  const checklist: Checklist = {
    id: checklistIdMapping.checklistId,
    title: template.title,
    imoNumber: template.imoNumber,
    isDrill,
    status: ChecklistStatus.InProgress,
    type: template.categoryName ?? canonical.categoryName ?? undefined,
    department:
      template.departmentName ?? canonical.departmentName ?? undefined,
    logbookTemplateId: canonical.logbookTemplateId ?? undefined,
    sections,
    started: entryTime,
    lastModified: undefined,
    finalized: undefined,
  };

  return [
    withDerivedProgress(checklist),
    checklistIdMapping,
    initialRepeatableOccurrences,
  ];
}

export function synthesizeRepeatableOccurrence(
  checklist: Checklist,
  sectionId: string,
  prototype: ChecklistItem
): {
  occurrence: ChecklistItem;
  idTree: RepeatableItemIdTree;
  customInputIdMap: Record<string, string> | null;
  patchedChecklist: Checklist;
} {
  const repeatableGroupId = prototype.repeatableGroupId ?? prototype.id;
  const section = checklist.sections.find(s => s.id === sectionId);
  const existingPositions = (section?.items ?? [])
    .filter(
      i =>
        !i.isRepeatablePrototype &&
        i.repeatableGroupId === repeatableGroupId &&
        typeof i.repeatableOccurrencePosition === 'number'
    )
    .map(i => i.repeatableOccurrencePosition as number);
  const nextPosition =
    existingPositions.length > 0 ? Math.max(...existingPositions) + 1 : 1;

  const { occurrence, idTree, customInputIdMap } = buildOccurrence(
    prototype,
    sectionId,
    nextPosition,
    repeatableInputValues(section?.items ?? [], prototype)
  );

  const patchedChecklist: Checklist = withDerivedProgress({
    ...checklist,
    sections: checklist.sections.map(s =>
      s.id === sectionId ? { ...s, items: [...s.items, occurrence] } : s
    ),
  });

  return { occurrence, idTree, customInputIdMap, patchedChecklist };
}

function buildOccurrence(
  prototype: ChecklistItem,
  sectionId: string,
  position: number,
  initialCustomInputPayloads: Record<string, RepeatableInputPayload>
): {
  occurrence: ChecklistItem;
  idTree: RepeatableItemIdTree;
  customInputIdMap: Record<string, string> | null;
} {
  const occurrenceId = uuidv4();

  const fieldIds: Record<string, string> = {};
  const fields = (prototype.fields ?? []).map(field => {
    const newFieldId = uuidv4();
    fieldIds[field.id] = newFieldId;
    return {
      id: newFieldId,
      itemId: occurrenceId,
      type: field.type ?? ChecklistFieldType.Text,
      position: field.position,
      data: structuredClone(field.data),
    };
  });

  let customInputIdMap: Record<string, string> | null = null;
  const customInputs = (prototype.customInputs ?? []).map(customInput => {
    const newCustomInputId = uuidv4();
    customInputIdMap ??= {};
    customInputIdMap[customInput.id] = newCustomInputId;
    return {
      id: newCustomInputId,
      itemId: occurrenceId,
      customInputTypeId: customInput.customInputTypeId,
      inputContent: customInput.inputContent,
      position: customInput.position,
      payload: initialCustomInputPayloads[customInput.id]
        ? JSON.stringify(initialCustomInputPayloads[customInput.id])
        : 'null',
      sourceCustomInputId: customInput.sourceCustomInputId,
      requiredInput: customInput.requiredInput ?? false,
      slotId: customInput.slotId,
    };
  });

  const slotSelections = (prototype.slotSelections ?? []).map(slot => ({
    itemId: occurrenceId,
    slotId: slot.slotId,
    position: slot.position,
    metadata: 'null',
    payload: 'null',
  }));

  const occurrence: ChecklistItem = {
    id: occurrenceId,
    sectionId,
    title: prototype.title,
    position: prototype.position,
    status: ChecklistItemStatus.Unchecked,
    logbookTemplateId: prototype.logbookTemplateId ?? undefined,
    logbookTemplateName: prototype.logbookTemplateName ?? undefined,
    remarks: undefined,
    statusChangedBy: undefined,
    fields,
    customInputs,
    slotSelections,
    isRepeatablePrototype: false,
    editableHeader: prototype.editableHeader === true,
    header: undefined,
    repeatableTitle: prototype.repeatableTitle ?? undefined,
    repeatableGroupId: prototype.repeatableGroupId ?? prototype.id,
    repeatableOccurrencePosition: position,
    statusChanged: undefined,
    entryTime: undefined,
    undoTime: undefined,
    completionTime: undefined,
  };

  const idTree: RepeatableItemIdTree = {
    occurrenceId,
    fieldIds,
    customInputIds: customInputIdMap,
    ...(Object.keys(initialCustomInputPayloads).length > 0
      ? { initialCustomInputPayloads }
      : {}),
  };

  return { occurrence, idTree, customInputIdMap };
}
